import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  booleanAttribute,
  inject
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { ENVIRONMENT, Environment } from '@environment';
import { Image } from '@usheru-hff/shared/domain';
import { SharedUiArrowModule } from '@usheru-hff/shared/ui-arrow';
import { SwiperDirective } from '@usheru-hff/shared/ui-swiper';
import { SharedUtilsModule } from '@usheru-hff/shared/utils';
import { SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { SharedUtilsTranslationModule } from '@usheru-hff/shared/utils-translation';

@Component({
  selector: 'feature-gallery-carousel',
  template: `
    <div class="gallery-carousel carousel">
      <!-- carousel header -->
      <div class="carousel-header">
        <div class="carousel-header__content">
          <h2 class="carousel-title" [attr.data-translate]="title">{{ title | translate: { placeholder: title } }}</h2>
        </div>
        <!-- end carousel header -->
        <!-- arrows -->
        <div class="arrows" *ngIf="!hideArrows">
          <ush-arrow
            [template]="arrowTemplate"
            class="gallery-{{ imageOrientation }}-left arrow"
            [orientation]="'left'"
          ></ush-arrow>
          <ush-arrow
            [template]="arrowTemplate"
            class="gallery-{{ imageOrientation }}-right arrow"
            [orientation]="'right'"
          ></ush-arrow>
          <!-- <button pButton class="gallery-{{ imageOrientation }}-left arrow" icon="pi pi-arrow-left"></button>
          <button pButton class="gallery-{{ imageOrientation }}-right arrow" icon="pi pi-arrow-right"></button> -->
        </div>
        <!-- end arrows -->
      </div>
      <div class="gallery-carousel__images">
        <!-- movies carousel -->
        <swiper-container ushSwiperElement [config]="swiperConfig" init="false">
          <swiper-slide *ngFor="let imgUrl of imgUrlsList" lazy="true">
            <img
              [ngSrc]="imgUrl"
              style="aspect-ratio: {{ aspectRatio }};"
              width="{{ imgWidth }}"
              height="{{ imgHeight }}"
              sizes="{{ swiperBasedSizes }}"
              alt="movie gallery carousel"
              [modal]="{
                src: imgUrl,
                type: 'landscape',
                movie: undefined,
                imgType: imageOrientation === 'landscape' ? 'tile-img' : 'poster-img'
              }"
            />
          </swiper-slide>
        </swiper-container>
      </div>
    </div>
  `,
  styleUrls: ['./feature-gallery-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    SwiperDirective,
    SharedUiArrowModule,
    SharedUtilsModule,
    ButtonModule,
    SharedUtilsImageModule,
    SharedUtilsTranslationModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FeatureGalleryCarouselComponent implements OnInit {
  @Input() imageOrientation: 'portrait' | 'landscape' = 'landscape';
  @Input() images: string[] | Image[];
  @Input() title: string = 'gallery';
  @Input() slidesPerView?: {
    0: number;
    480: number;
    1024: number;
    1400: number;
  };
  @Input() autoplay?: boolean;
  @Input() arrowTemplate?: number;
  @Input({ transform: booleanAttribute }) hideArrows?: boolean;

  imgUrlsList: string[];

  aspectRatio: string;
  imgWidth: number = 200;
  imgHeight: number;
  swiperConfig: SwiperOptions;
  swiperBasedSizes: string;

  arrowTemplateDefault = 5;

  private env: Environment = inject(ENVIRONMENT);

  ngOnInit(): void {
    this.title =
      this.imageOrientation === 'portrait' ? $localize`:@@title.posters:Posters` : $localize`:@@title.gallery:Gallery`;
    this.imgUrlsList = (this.images[0] as Image).imageUrl
      ? (this.images as Image[]).map((img: Image) => img.imageUrl)
      : (this.images as string[]);
    this.aspectRatio =
      this.imageOrientation === 'portrait'
        ? 'var(--aspect-ratio-portrait, 2/3)'
        : 'var(--aspect-ratio-landscape, 16/9)';
    this.imgWidth = this.imageOrientation === 'portrait' ? 200 : 160;
    this.imgHeight = this.imageOrientation === 'portrait' ? 300 : 90;
    this.setUpArrowTemplate();
    this.swiperConfig = {
      slidesPerView:
        (this.slidesPerView && this.slidesPerView['0']) || (this.imageOrientation === 'landscape' ? 1.4 : 2.4),
      spaceBetween: 10,
      scrollbar: true,
      breakpoints: {
        // when window width is >= 480px
        480: {
          slidesPerView:
            (this.slidesPerView && this.slidesPerView['480']) || (this.imageOrientation === 'landscape' ? 2.4 : 3.4),
          spaceBetween: 20
        },
        1024: {
          slidesPerView:
            (this.slidesPerView && this.slidesPerView['1024']) || (this.imageOrientation === 'landscape' ? 3 : 5),
          spaceBetween: 30
        },
        1400: {
          slidesPerView:
            (this.slidesPerView && this.slidesPerView['1400']) || (this.imageOrientation === 'landscape' ? 4 : 6),
          spaceBetween: 30
        }
      },
      navigation: {
        nextEl: '.gallery-' + this.imageOrientation + '-right',
        prevEl: '.gallery-' + this.imageOrientation + '-left'
      },
      autoplay: this.autoplay ? { delay: 3000, pauseOnMouseEnter: true } : false
    };
    this.swiperBasedSizes = this.getResponsiveSizesString(this.swiperConfig.breakpoints, 'slidesPerView');
  }

  private setUpArrowTemplate() {
    if (this.arrowTemplate) return;

    this.arrowTemplate =
      this.env.carouselTypeParams?.gallery?.arrowLayoutDesing?.top ||
      this.env.carouselTypeParams?.gallery?.arrowLayoutDesing?.down ||
      this.env.carouselTypeParams?.gallery?.arrowLayoutDesing?.side ||
      this.env.carouselTypeParams?.gallery?.arrowLayoutDesing?.inside ||
      this.arrowTemplateDefault;
  }

  private getResponsiveSizesString(breakpoints: any, property: string): string {
    let sizeString = '';
    const sortedBreakpoints = Object.keys(breakpoints).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)); // Sort breakpoints numerically

    for (const breakpoint of sortedBreakpoints) {
      const breakpointValue = parseInt(breakpoint, 10);
      const config = breakpoints[breakpoint];
      const value = config[property];

      if (breakpointValue > 0) {
        // Skip the initial breakpoint (usually 0)
        sizeString += `(max-width: ${breakpointValue - 1}px) ${value}vw, `; // Adjust for inclusive breakpoints
      }
    }

    // Remove the trailing comma
    return sizeString.slice(0, -2);
  }
}
